import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X, Eye, EyeSlash, Link, QrCode, Play, ChartBar, PencilSimple, MagicWand, ClipboardText, Robot, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import CouponEditor from "../components/CouponEditor";

const TIPO_BADGE = {
  promozionale: "bg-emerald-100 text-emerald-800 border-emerald-300",
  referral: "bg-violet-100 text-violet-800 border-violet-300",
  vetrina: "bg-amber-100 text-amber-800 border-amber-300",
};

const fmtDate = (iso) => {
  const [y, m, d] = String(iso || "").slice(0, 10).split("-");
  return y ? `${d}/${m}/${y}` : "";
};

export default function Coupon() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const rid = user?.restaurant_id;
  const [tab, setTab] = useState("definizioni");
  const [coupons, setCoupons] = useState([]);
  const [customerCoupons, setCustomerCoupons] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [webForms, setWebForms] = useState([]);
  const [showMode, setShowMode] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editorInitial, setEditorInitial] = useState(null);
  const [showDownload, setShowDownload] = useState(null);
  const [statsFor, setStatsFor] = useState(null);
  const [ccList, setCcList] = useState(null);
  const [ccSearch, setCcSearch] = useState(""); // filtro live tab "Coupon clienti"
  const [form, setForm] = useState({ name: "", description: "", valid_from: "", valid_to: "", conditions: "", campaign_id: "" });
  const [dlForm, setDlForm] = useState({ phone: "", first_name: "", last_name: "", email: "" });

  const load = useCallback(() => {
    api.get("/coupons").then((r) => setCoupons(r.data)).catch(() => {});
    api.get("/customer-coupons").then((r) => setCustomerCoupons(r.data)).catch(() => {});
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
    api.get("/web-forms").then((r) => setWebForms(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadCcList = (couponId) =>
    api.get("/customer-coupons", { params: { coupon_id: couponId } })
      .then((r) => setCcList(r.data)).catch(() => setCcList([]));

  useEffect(() => {
    if (!statsFor) { setCcList(null); return; }
    loadCcList(statsFor.id);
  }, [statsFor]);

  const markUsed = async (cc) => {
    try {
      const r = await api.post("/coupons/redeem", { code: cc.code });
      toast.success(r.data.reservation_linked ? "Coupon utilizzato e collegato alla prenotazione di oggi" : "Coupon segnato come utilizzato");
      load();
      if (statsFor) loadCcList(statsFor.id);
    } catch (err) { toast.error(apiError(err)); }
  };

  const removeCc = async (cc) => {
    if (!window.confirm(`Rimuovere il coupon ${cc.code} di ${cc.customer_name}?`)) return;
    try {
      await api.delete(`/customer-coupons/${cc.id}`);
      toast.success("Coupon rimosso");
      load();
      if (statsFor) loadCcList(statsFor.id);
    } catch (err) { toast.error(apiError(err)); }
  };

  const publicUrl = (c) => `${window.location.origin}/p/${rid}/c/${c.slug}`;

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/coupons", { ...form, campaign_id: form.campaign_id || null });
      toast.success("Coupon creato");
      setShowNew(false);
      setForm({ name: "", description: "", valid_from: "", valid_to: "", conditions: "", campaign_id: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const download = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post(`/coupons/${showDownload.id}/download`, dlForm);
      toast.success(`Coupon assegnato — codice ${r.data.code}`);
      setShowDownload(null);
      setDlForm({ phone: "", first_name: "", last_name: "", email: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const toggle = async (c) => {
    try {
      const r = await api.post(`/coupons/${c.id}/toggle`);
      toast.success(r.data.active ? "Coupon attivato" : "Coupon disattivato");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const copyLink = async (c) => {
    try {
      await navigator.clipboard.writeText(publicUrl(c));
      toast.success("Link pubblico del coupon copiato (usalo nelle ads)");
    } catch { toast.error("Copia non riuscita"); }
  };

  const downloadQr = async (c) => {
    try {
      const r = await api.get("/tools/qr", { params: { content: publicUrl(c) }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `qr-coupon-${c.slug}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openAutomation = async (c) => {
    try {
      const r = await api.post(`/coupons/${c.id}/automation`);
      navigate(`/automazioni?edit=${r.data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const aiGenerate = async () => {
    if (aiPrompt.trim().length < 5) { toast.error("Descrivi la promozione che vuoi creare"); return; }
    setAiLoading(true);
    try {
      const r = await api.post("/coupons/ai-generate", { prompt: aiPrompt });
      setEditorInitial(r.data);
      setShowAI(false);
      setShowEditor(true);
      setAiPrompt("");
      toast.success("Bozza generata — rivedila e salva");
    } catch (e) { toast.error(apiError(e)); }
    finally { setAiLoading(false); }
  };

  const openEditor = (c) => { setEditorInitial(c); setShowEditor(true); };

  const removeCoupon = async (c) => {
    if (!window.confirm(`Eliminare il coupon "${c.name}"?\n\nSparirà dalla lista e dalle pagine pubbliche. I coupon già scaricati dai clienti restano validi in cassa (storico conservato).`)) return;
    try {
      await api.delete(`/coupons/${c.id}`);
      toast.success("Coupon eliminato");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const inputCls = "w-full h-11 px-3 rounded-md border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 bg-white";
  const labelCls = "block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1";

  return (
    <div className="space-y-4" data-testid="coupon-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Coupon</h1>
        <button onClick={() => setShowMode(true)} data-testid="btn-nuovo-coupon"
          className="h-11 px-4 rounded-md bg-zinc-900 text-white text-sm font-semibold flex items-center gap-2">
          <Plus size={16} weight="bold" /> Nuovo coupon
        </button>
      </div>

      <div className="flex gap-1.5">
        {[["definizioni", "Definizioni"], ["clienti", `Coupon clienti (${customerCoupons.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`h-9 px-4 rounded-full text-xs font-semibold border ${tab === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "definizioni" && (
        <div className="space-y-2" data-testid="coupon-defs">
          {coupons.length === 0 && <div className="text-sm text-zinc-400 py-6">Nessun coupon definito</div>}
          {coupons.map((c) => (
            <div key={c.id} className="bg-white border border-zinc-200 rounded-lg p-4" data-testid={`coupon-def-${c.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase ${TIPO_BADGE[c.tipo] || TIPO_BADGE.promozionale}`}
                  data-testid={`coupon-tipo-${c.id}`}>{c.tipo === "referral" ? "REFERRAL" : c.tipo === "vetrina" ? "VETRINA" : "PROMO"}</span>
                <span className="font-display font-bold text-zinc-900">{c.name}</span>
                {c.web_form_id && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200"
                    data-testid={`coupon-modulo-${c.id}`}>Modulo collegato</span>
                )}
                <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full border ${c.active ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-zinc-100 text-zinc-500 border-zinc-300"}`}>
                  {c.active ? "ATTIVO" : "NON ATTIVO"}
                </span>
              </div>
              <div className="text-xs text-zinc-500 mt-1.5 flex items-center gap-3 flex-wrap">
                {c.description && <span className="truncate">{c.description}</span>}
                <span>Valido: {c.valid_from} → {c.valid_to}</span>
                <span className="font-mono truncate">/p/{rid}/c/{c.slug}</span>
              </div>
              <div className="flex gap-1.5 mt-3 flex-wrap items-center">
                <button onClick={() => toggle(c)} title={c.active ? "Coupon attivo — clicca per disattivare" : "Coupon non attivo — clicca per attivare"}
                  data-testid={`coupon-toggle-${c.id}`}
                  className={`h-9 w-9 rounded-md flex items-center justify-center border transition ${c.active ? "bg-white border-zinc-300 text-zinc-600 hover:border-rose-300 hover:text-rose-600" : "bg-emerald-600 border-emerald-600 text-white"}`}>
                  {c.active ? <Eye size={15} /> : <EyeSlash size={15} />}
                </button>
                <button onClick={() => copyLink(c)} title="Copia link pubblico (per le ads)" data-testid={`coupon-link-${c.id}`}
                  className="h-9 w-9 rounded-md bg-white border border-zinc-300 text-zinc-600 flex items-center justify-center hover:border-zinc-400">
                  <Link size={15} />
                </button>
                <button onClick={() => downloadQr(c)} title="Scarica QR code" data-testid={`coupon-qr-${c.id}`}
                  className="h-9 w-9 rounded-md bg-white border border-zinc-300 text-zinc-600 flex items-center justify-center hover:border-zinc-400">
                  <QrCode size={15} />
                </button>
                <button onClick={() => openAutomation(c)} title="Automazione all'evento coupon scaricato" data-testid={`coupon-play-${c.id}`}
                  className="h-9 w-9 rounded-md bg-white border border-zinc-300 text-emerald-600 flex items-center justify-center hover:border-emerald-400">
                  <Play size={15} weight="fill" />
                </button>
                <button onClick={() => setStatsFor(c)} title="Assegnati: vedi chi ha scaricato il coupon" data-testid={`coupon-stats-${c.id}`}
                  className="h-9 px-2.5 rounded-md bg-white border border-zinc-300 text-zinc-600 text-xs font-semibold flex items-center gap-1 hover:border-zinc-400">
                  <ChartBar size={15} /> {c.downloaded}/{c.used}
                </button>
                <button onClick={() => setShowDownload(c)} data-testid={`btn-download-${c.id}`}
                  className="h-9 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 hover:border-zinc-400">
                  Assegna
                </button>
                <button onClick={() => openEditor(c)} data-testid={`coupon-edit-${c.id}`}
                  className="h-9 px-3 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5">
                  <PencilSimple size={13} weight="bold" /> Modifica
                </button>
                <button onClick={() => removeCoupon(c)} title="Elimina coupon" data-testid={`coupon-delete-${c.id}`}
                  className="h-9 w-9 rounded-md bg-white border border-rose-300 text-rose-600 flex items-center justify-center hover:bg-rose-50 transition">
                  <Trash size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "clienti" && (
        <div data-testid="customer-coupons-section">
          <input value={ccSearch} onChange={(e) => setCcSearch(e.target.value)}
            placeholder="Cerca per nome, telefono, coupon o codice..." data-testid="cc-search"
            className="w-full mb-3 px-3 py-2.5 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900" />
          {(() => {
            const q = ccSearch.trim().toLowerCase();
            const filtered = q ? customerCoupons.filter((cc) =>
              [cc.customer_name, cc.customer_phone, cc.coupon_name, cc.code]
                .some((v) => (v || "").toLowerCase().includes(q))) : customerCoupons;
            return (
        <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100" data-testid="customer-coupons-list">
          {customerCoupons.length === 0 && <div className="py-8 text-center text-sm text-zinc-400">Nessun coupon scaricato</div>}
          {customerCoupons.length > 0 && filtered.length === 0 && (
            <div className="py-8 text-center text-sm text-zinc-400" data-testid="cc-no-results">Nessun risultato per "{ccSearch}"</div>
          )}
          {filtered.map((cc) => (
            <div key={cc.id} className="flex items-center gap-3 px-4 py-3 flex-wrap" data-testid={`cc-${cc.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-zinc-900 truncate">{cc.coupon_name}</div>
                <div className="text-xs text-zinc-500">{cc.customer_name} · {cc.customer_phone}</div>
                <div className="text-[11px] text-zinc-400" data-testid={`cc-dates-${cc.id}`}>
                  Scaricato il {fmtDate(cc.downloaded_at || cc.created_at)}
                  {cc.scade_il && <> · Scade il <span className="font-semibold text-zinc-500">{fmtDate(cc.scade_il)}</span></>}
                </div>
              </div>
              <span className="font-mono text-xs bg-zinc-100 border border-zinc-200 rounded px-2 py-1">{cc.code}</span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                cc.status === "utilizzato" ? "bg-zinc-100 text-zinc-600 border-zinc-300"
                : cc.status === "attivo" ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                : "bg-rose-100 text-rose-700 border-rose-300"}`} data-testid={`cc-status-${cc.id}`}>{cc.status}</span>
              {cc.status === "attivo" && (
                <button onClick={() => markUsed(cc)} data-testid={`cc-use-${cc.id}`}
                  className="h-8 px-3 rounded-md bg-emerald-600 text-white text-xs font-bold active:scale-95 transition">
                  Segna utilizzato
                </button>
              )}
            </div>
          ))}
        </div>
            );
          })()}
        </div>
      )}

      {showMode && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={() => setShowMode(false)}>
          <div className="bg-white w-full sm:max-w-lg rounded-xl p-5" onClick={(e) => e.stopPropagation()} data-testid="coupon-mode-dialog">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display font-bold text-lg">Crea coupon</h3>
              <button onClick={() => setShowMode(false)} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            <p className="text-xs text-zinc-500 mb-4">Seleziona la modalità di creazione</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <button onClick={() => { setShowMode(false); setShowNew(true); }} data-testid="coupon-mode-semplificata"
                className="border border-zinc-200 rounded-lg p-4 text-left hover:border-zinc-900 transition group">
                <MagicWand size={22} className="text-zinc-700 group-hover:text-zinc-900" />
                <div className="font-semibold text-sm text-zinc-900 mt-2">Creazione automatica semplificata</div>
                <p className="text-[11px] text-zinc-500 mt-1">Crea rapidamente un coupon con le impostazioni essenziali.</p>
              </button>
              <button onClick={() => { setShowMode(false); setEditorInitial(null); setShowEditor(true); }} data-testid="coupon-mode-manuale"
                className="border border-zinc-200 rounded-lg p-4 text-left hover:border-zinc-900 transition group">
                <ClipboardText size={22} className="text-zinc-700 group-hover:text-zinc-900" />
                <div className="font-semibold text-sm text-zinc-900 mt-2">Creazione manuale completa</div>
                <p className="text-[11px] text-zinc-500 mt-1">Tutte le impostazioni: campi, landing, thank-you e modulo collegato.</p>
              </button>
              <button onClick={() => { setShowMode(false); setShowAI(true); }} data-testid="coupon-mode-ai"
                className="border-2 border-amber-400 rounded-lg p-4 text-left hover:border-amber-500 transition group bg-amber-50/50">
                <Robot size={22} className="text-amber-600" />
                <div className="font-semibold text-sm text-zinc-900 mt-2">Creazione automatica con AI</div>
                <p className="text-[11px] text-zinc-500 mt-1">Descrivi la promozione e attendi la generazione.</p>
              </button>
            </div>
          </div>
        </div>
      )}

      {showAI && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={() => !aiLoading && setShowAI(false)}>
          <div className="bg-white w-full sm:max-w-md rounded-xl p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="coupon-ai-dialog">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg flex items-center gap-2"><Robot size={20} className="text-amber-600" /> Assistente AI</h3>
              <button onClick={() => setShowAI(false)} disabled={aiLoading} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            <p className="text-xs text-zinc-500">Descrivi la promozione in linguaggio naturale. L'AI prepara nome, codice, landing e thank-you page: potrai rivedere tutto prima di salvare.</p>
            <textarea rows={4} value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} data-testid="coupon-ai-prompt"
              placeholder='Es. "Dolce omaggio a cena dal lunedì al giovedì per tutto ottobre, link da usare nelle ads Facebook"'
              className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900" />
            <button onClick={aiGenerate} disabled={aiLoading} data-testid="coupon-ai-generate"
              className="w-full h-12 rounded-md bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 active:scale-[0.98] transition disabled:opacity-50">
              {aiLoading ? "Generazione in corso..." : "Genera bozza coupon"}
            </button>
          </div>
        </div>
      )}

      {showEditor && (
        <CouponEditor initial={editorInitial} campaigns={campaigns} webForms={webForms}
          onClose={() => setShowEditor(false)} onSaved={load} />
      )}

      {statsFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={() => setStatsFor(null)}>
          <div className="bg-white rounded-xl p-5 w-full sm:max-w-lg max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="coupon-stats-dialog">
            <h3 className="font-display font-bold text-zinc-900">Assegnati — {statsFor.name}</h3>
            <div className="grid grid-cols-3 gap-3 mt-3 text-center">
              <div>
                <div className="font-display text-2xl font-bold text-zinc-900" data-testid="coupon-stats-scaricati">{statsFor.downloaded || 0}</div>
                <div className="text-[11px] text-zinc-500">scaricati</div>
              </div>
              <div>
                <div className="font-display text-2xl font-bold text-zinc-900" data-testid="coupon-stats-utilizzati">{statsFor.used || 0}</div>
                <div className="text-[11px] text-zinc-500">utilizzati</div>
              </div>
              <div>
                <div className="font-display text-2xl font-bold text-emerald-700" data-testid="coupon-stats-referral">{statsFor.referred || 0}</div>
                <div className="text-[11px] text-zinc-500">da condivisione</div>
              </div>
            </div>
            <div className="mt-4 overflow-y-auto flex-1 divide-y divide-zinc-100" data-testid="coupon-downloads-list">
              {ccList === null && <div className="text-xs text-zinc-400 py-6 text-center">Caricamento...</div>}
              {ccList !== null && ccList.length === 0 && (
                <div className="text-xs text-zinc-400 py-6 text-center">Nessun cliente ha ancora scaricato questo coupon</div>
              )}
              {(ccList || []).map((cc) => (
                <div key={cc.id} className="flex items-center gap-3 py-2.5" data-testid={`dl-row-${cc.id}`}>
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/public/coupon-qr/${rid}/${cc.code}`}
                    alt={`QR ${cc.code}`} className="w-14 h-14 rounded border border-zinc-200 shrink-0"
                    data-testid={`dl-qr-${cc.id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-zinc-900 truncate" data-testid={`dl-name-${cc.id}`}>{cc.customer_name}</div>
                    <div className="font-mono text-xs text-zinc-500">{cc.code}</div>
                    <div className="text-[11px] text-zinc-400" data-testid={`dl-dates-${cc.id}`}>
                      Scaricato il {fmtDate(cc.downloaded_at || cc.created_at)}
                      {cc.scade_il && <> · Scade il <span className="font-semibold text-zinc-500">{fmtDate(cc.scade_il)}</span></>}
                    </div>
                    {cc.referred_by_name && (
                      <div className="text-[11px] font-semibold text-amber-700" data-testid={`dl-ref-${cc.id}`}>
                        🎁 Invitato da {cc.referred_by_name}
                      </div>
                    )}
                  </div>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border shrink-0 ${
                    cc.status === "utilizzato" ? "bg-zinc-100 text-zinc-600 border-zinc-300"
                    : cc.status === "attivo" ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                    : "bg-rose-100 text-rose-700 border-rose-300"}`} data-testid={`dl-status-${cc.id}`}>{cc.status}</span>
                  {cc.status === "attivo" && (
                    <button onClick={() => markUsed(cc)} data-testid={`dl-redeem-${cc.id}`}
                      className="h-8 px-2.5 rounded-md bg-emerald-600 text-white text-xs font-bold shrink-0 active:scale-95 transition">
                      Riscatta
                    </button>
                  )}
                  <button onClick={() => removeCc(cc)} data-testid={`dl-remove-${cc.id}`}
                    className="h-8 px-2.5 rounded-md bg-white border border-rose-300 text-rose-600 text-xs font-bold shrink-0 hover:bg-rose-50 active:scale-95 transition">
                    Rimuovi
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => setStatsFor(null)} data-testid="coupon-stats-close"
              className="mt-4 w-full h-10 rounded-md bg-zinc-900 text-white text-sm font-semibold shrink-0">Chiudi</button>
          </div>
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setShowNew(false)}>
          <form onSubmit={create} className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid="coupon-form">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">Nuovo coupon (rapido)</h3>
              <button type="button" onClick={() => setShowNew(false)} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            <div><label className={labelCls}>Nome *</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="coupon-name" /></div>
            <div><label className={labelCls}>Descrizione</label>
              <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} data-testid="coupon-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Valido dal *</label>
                <input type="date" required value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} className={inputCls} data-testid="coupon-from" /></div>
              <div><label className={labelCls}>Valido al *</label>
                <input type="date" required value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} className={inputCls} data-testid="coupon-to" /></div>
            </div>
            <div><label className={labelCls}>Condizioni</label>
              <input value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} className={inputCls} data-testid="coupon-conditions" /></div>
            <div><label className={labelCls}>Campagna di origine</label>
              <select value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })} className={inputCls} data-testid="coupon-campaign">
                <option value="">Nessuna</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
            <button className="w-full h-12 rounded-md bg-zinc-900 text-white text-sm font-semibold" data-testid="coupon-submit">Crea coupon</button>
          </form>
        </div>
      )}

      {showDownload && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setShowDownload(null)}>
          <form onSubmit={download} className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-lg p-5 space-y-3"
            onClick={(e) => e.stopPropagation()} data-testid="download-form">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">Assegna: {showDownload.name}</h3>
              <button type="button" onClick={() => setShowDownload(null)} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            <p className="text-xs text-zinc-500">Se il telefono esiste già, il coupon verrà collegato al cliente esistente (niente duplicati).</p>
            <div><label className={labelCls}>Telefono *</label>
              <input required value={dlForm.phone} onChange={(e) => setDlForm({ ...dlForm, phone: e.target.value })} className={inputCls} data-testid="dl-phone" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Nome</label>
                <input value={dlForm.first_name} onChange={(e) => setDlForm({ ...dlForm, first_name: e.target.value })} className={inputCls} data-testid="dl-name" /></div>
              <div><label className={labelCls}>Cognome</label>
                <input value={dlForm.last_name} onChange={(e) => setDlForm({ ...dlForm, last_name: e.target.value })} className={inputCls} data-testid="dl-lastname" /></div>
            </div>
            <div><label className={labelCls}>Email</label>
              <input type="email" value={dlForm.email} onChange={(e) => setDlForm({ ...dlForm, email: e.target.value })} className={inputCls} data-testid="dl-email" /></div>
            <button className="w-full h-12 rounded-md bg-zinc-900 text-white text-sm font-semibold" data-testid="dl-submit">Assegna coupon</button>
          </form>
        </div>
      )}
    </div>
  );
}
